import { Liquid, Output } from "liquidjs";
import { createCepLiquidEngine } from "@hightouch/lib/cep/cep-liquid-engine";
import { containsLiquidSyntax } from "./liquid-syntax";

export { containsLiquidSyntax };

// AST introspection below reads liquidjs v10 internal node shapes. No filters
// registered — parsing is purely syntactic. The app's `liquidjs` is pinned to
// the same version the backend renders with (see @hightouch/lib/templating).
const parseEngine = new Liquid();

// Preview rendering goes through the shared CEP engine to match the send path
// exactly (same wrapper, same liquidjs version, same filters).
const renderEngine = createCepLiquidEngine();

export type LiquidVariableFilter = {
  name: string;
  args: (string | { key?: string; value: string; numeric?: boolean })[];
};

export type LiquidVariable = {
  variableName: string;
  filters: LiquidVariableFilter[];
  raw: string;
  // True when the base is a quoted string literal (`{{ "now" }}`) rather than a
  // variable lookup — the inverse of `formatMergeTagWithFilters`' `literalBase`.
  // Absent for ordinary variables. The picker's Dates presets are the only
  // literal-base tags, so this doubles as "is a date preset" on reopen.
  literalBase?: boolean;
};

export type LiquidSegment =
  | { type: "text"; value: string }
  | ({ type: "variable" } & LiquidVariable);

// liquidjs internal token kind for a quoted string literal — used for
// bracket-form variable names like {{ ["first name"] }}.
const QUOTED_TOKEN_KIND = 1024;

function extractVariable(initial: unknown): {
  name: string;
  literalBase: boolean;
} {
  // Accesses liquidjs v10 internal AST (not public API). The Output node's
  // `value.initial` is a VariableSegment whose postfix[0].props holds the
  // variable name segments. If liquidjs changes this shape, the
  // parseLiquidTemplate/parseLiquidVariables tests will catch it.
  if (initial && typeof initial === "object") {
    const postfix = (initial as Record<string, unknown>).postfix;
    const firstTok = Array.isArray(postfix) ? postfix[0] : undefined;
    if (firstTok?.kind === QUOTED_TOKEN_KIND && !firstTok.props) {
      // String literal like {{ "foo" }} — postfix[0] IS the quoted token.
      return { name: String(firstTok.content), literalBase: true };
    }
    const props = firstTok?.props;
    if (Array.isArray(props) && props.length > 0) {
      // Bracket form {{ ["first name"] }} — the prop is a quoted string token
      // whose content is the full variable name (including spaces etc.).
      if (props[0]?.kind === QUOTED_TOKEN_KIND && props[0].content != null) {
        return { name: String(props[0].content), literalBase: false };
      }
      // Dotted form {{ user.name }} — each prop is a segment; join with dots.
      return {
        name: props
          .map((p) => (p?.content != null ? String(p.content) : ""))
          .join("."),
        literalBase: false,
      };
    }
  }
  return { name: String(initial), literalBase: false };
}

const NUMERIC_ARG_RE = /^-?\d+(\.\d+)?$/;

// Escape for a double-quoted Liquid string literal. Backslashes must be escaped
// before quotes, otherwise a lone `\` produces `"\""` (the closing quote gets
// escaped → unterminated string) and a value could break out of the `{{ }}`.
function escapeLiquidString(value: string): string {
  return value.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
}

function quoteArg(value: string): string {
  return `"${escapeLiquidString(value)}"`;
}

type FilterArg = LiquidVariableFilter["args"][number];

// Whether quoting is decided by the arg's declared type or inferred from shape:
// a `numeric` field emits unquoted (so `truncate: 20` / `slice: 0, 4` are valid
// Liquid), a text field is always quoted so numeric-looking text ("007", "01")
// keeps its string value instead of being coerced to a number at render. Also
// reused to render a filter's arguments in human-facing labels.
export function serializeFilterArgValue(arg: FilterArg): string {
  if (typeof arg === "string") {
    return NUMERIC_ARG_RE.test(arg) ? arg : quoteArg(arg);
  }
  return arg.numeric && NUMERIC_ARG_RE.test(arg.value)
    ? arg.value
    : quoteArg(arg.value);
}

function argKey(arg: FilterArg): string | undefined {
  return typeof arg === "string" ? undefined : arg.key;
}

// A rendered piece of a merge-tag snippet. `highlight` marks the value tokens —
// the variable base and each filter argument value — so the UI can color them
// distinctly from the surrounding Liquid syntax ({{ }}, pipes, filter names,
// punctuation). The segment texts concatenate to exactly the string
// `formatMergeTagWithFilters` returns; both are built here so they can't drift.
export type MergeTagSnippetSegment = { text: string; highlight: boolean };

const syntax = (text: string): MergeTagSnippetSegment => ({
  text,
  highlight: false,
});
const value = (text: string): MergeTagSnippetSegment => ({
  text,
  highlight: true,
});

// Base is bracket-access (`["name"]`) by default — required for names Liquid
// can't parse as identifiers (spaces, hyphens), and used uniformly for
// consistency. `literalBase` instead emits a quoted string literal (`"now"`) for
// non-column values consumed by a filter — notably the Dates presets
// (`{{ "now" | date: ... }}`, where `"now"` is a literal, not a variable).
export function mergeTagSnippetSegments(
  variableName: string,
  filters: LiquidVariableFilter[],
  opts?: { literalBase?: boolean },
): MergeTagSnippetSegment[] {
  const escaped = escapeLiquidString(variableName);
  const base = opts?.literalBase ? `"${escaped}"` : `["${escaped}"]`;
  const segments: MergeTagSnippetSegment[] = [syntax("{{ "), value(base)];
  for (const filter of filters) {
    segments.push(syntax(" | "));
    if (filter.args.length === 0) {
      segments.push(syntax(filter.name));
      continue;
    }
    segments.push(syntax(`${filter.name}: `));
    for (const [index, arg] of filter.args.entries()) {
      if (index > 0) segments.push(syntax(", "));
      const key = argKey(arg);
      if (key) segments.push(syntax(`${key}: `));
      segments.push(value(serializeFilterArgValue(arg)));
    }
  }
  segments.push(syntax(" }}"));
  return segments;
}

export function formatMergeTagWithFilters(
  variableName: string,
  filters: LiquidVariableFilter[],
  opts?: { literalBase?: boolean },
): string {
  return mergeTagSnippetSegments(variableName, filters, opts)
    .map((segment) => segment.text)
    .join("");
}

export function formatMergeTag(variableName: string): string {
  return formatMergeTagWithFilters(variableName, []);
}

// Shown when renderLiquidValuePreview returns undefined — the raw liquidjs error
// (ParseError/TypeError text) isn't marketer-friendly, so callers show this.
export const LIQUID_PREVIEW_ERROR_MESSAGE =
  "This transformation can't be rendered. Check the filter values.";

/**
 * Render a single-variable snippet against one sample value, for the transform
 * preview. Returns `undefined` when the snippet can't be parsed or rendered
 * synchronously — malformed syntax, or a filter that throws on the given input
 * (e.g. a bad numeric arg). `parseAndRenderSync` otherwise always yields a
 * string, so `undefined` unambiguously signals an error.
 */
export function renderLiquidValuePreview(
  snippet: string,
  variableName: string,
  value: string | null,
): string | undefined {
  try {
    return renderEngine.parseAndRenderSync(snippet, { [variableName]: value });
  } catch {
    return undefined;
  }
}

function extractArgValue(arg: unknown): string {
  // liquidjs v10 internal: filter args are token objects with .content
  if (arg && typeof arg === "object" && "content" in arg) {
    return String((arg as { content: unknown }).content);
  }
  return String(arg);
}

function parseVariable(node: Output): Omit<LiquidVariable, "raw"> {
  const { name, literalBase } = extractVariable(node.value.initial);
  return {
    variableName: name,
    ...(literalBase && { literalBase: true }),
    filters: node.value.filters.map((filter) => ({
      name: filter.name,
      args: filter.args.map((arg) =>
        Array.isArray(arg)
          ? { key: arg[0], value: extractArgValue(arg[1]) }
          : extractArgValue(arg),
      ),
    })),
  };
}

// Lazily matches {{ ... }} tags, stopping at the first }}. This ensures
// "{{ a }} {{ b }}" produces two matches instead of one, and unclosed tags
// (no closing }}) never match and fall through as plain text.
const LIQUID_TAG_RE = /\{\{([\s\S]*?)\}\}/g;

export function parseLiquidTemplate(template: string): LiquidSegment[] {
  if (!template) return [];
  const segments: LiquidSegment[] = [];
  let lastIndex = 0;

  for (const match of template.matchAll(LIQUID_TAG_RE)) {
    const matchIndex = match.index;
    if (matchIndex > lastIndex) {
      segments.push({
        type: "text",
        value: template.slice(lastIndex, matchIndex),
      });
    }

    try {
      const parsed = parseEngine.parse(match[0]);
      for (const node of parsed) {
        if (node instanceof Output) {
          segments.push({
            type: "variable",
            ...parseVariable(node),
            raw: (match[1] ?? "").trim(),
          });
        }
        // Tag nodes ({% %}) are ignored
      }
    } catch {
      segments.push({ type: "text", value: match[0] });
    }

    lastIndex = matchIndex + match[0].length;
  }

  if (lastIndex < template.length) {
    segments.push({ type: "text", value: template.slice(lastIndex) });
  }

  return segments;
}

export function parseLiquidVariables(template: string): LiquidVariable[] {
  return parseLiquidTemplate(template).flatMap((s) => {
    if (s.type !== "variable") return [];
    const { type: _type, ...variable } = s;
    return [variable];
  });
}

/** Extract the first argument of the `default` filter, if present and a string. */
export function getDefaultFilterValue(
  filters: LiquidVariableFilter[],
): string | undefined {
  const defaultFilter = filters.find((f) => f.name === "default");
  return typeof defaultFilter?.args[0] === "string"
    ? defaultFilter.args[0]
    : undefined;
}

/**
 * Set or update the `| default:` filter for a specific variable in a liquid
 * template string. If `value` is empty, removes the filter.
 *
 * Matches tags by variable name (the bracket-form `["alias"]` content).
 */
export function setDefaultFilter(
  template: string,
  variableName: string,
  value: string,
): string {
  return template.replace(LIQUID_TAG_RE, (match) => {
    try {
      const parsed = parseEngine.parse(match);
      for (const node of parsed) {
        if (node instanceof Output) {
          const { name } = extractVariable(node.value.initial);
          if (name !== variableName) return match;

          const inner = (match.match(/\{\{([\s\S]*?)\}\}/)?.[1] ?? "").trim();

          const escaped = value ? escapeLiquidString(value) : "";
          const newDefault = value ? `| default: "${escaped}"` : "";
          const dqPattern = /\|\s*default:\s*"(?:[^"\\]|\\.)*"/;
          const sqPattern = /\|\s*default:\s*'(?:[^'\\]|\\.)*'/;

          let result: string;
          if (dqPattern.test(inner)) {
            result = inner.replace(dqPattern, newDefault);
          } else if (sqPattern.test(inner)) {
            result = inner.replace(sqPattern, newDefault);
          } else if (value) {
            result = `${inner} | default: "${escaped}"`;
          } else {
            result = inner;
          }

          return `{{ ${result.replace(/\s+/g, " ").trim()} }}`;
        }
      }
    } catch {
      // Not a valid liquid tag, leave as-is
    }
    return match;
  });
}

/**
 * Strip incompatible liquid syntax for preview
 * Used when platform-specific syntax can't be rendered with liquidjs
 */
function stripLiquidForPreview(html: string): string {
  let result = html;

  // Replace liquid output tags with placeholder or default value
  // Updated regex to handle nested braces like {{ api_trigger_properties.${hightouch}.field }}
  result = result.replace(/\{\{[\s\S]*?\}\}/g, (match): string => {
    // First, check if there's a default value - use that if present
    // Matches: {{ ... | default: "value" }} or {{ ... | default: 'value' }}
    const defaultMatch = match.match(/\|\s*default:\s*["']([^"']*)["']/);
    if (defaultMatch && defaultMatch[1] !== undefined) {
      return defaultMatch[1]; // Return the default value
    }

    // Try to extract a meaningful variable name for placeholder
    // Handles: {{ variable }}, {{ object.property }}, {{ api_trigger_properties.${...}.field }}
    const varMatch = match.match(/\{\{\s*([a-zA-Z_][\w.${}]*)/);
    if (varMatch && varMatch[1]) {
      // Simplify complex nested properties for readability
      let varName = varMatch[1];

      // For patterns like "api_trigger_properties.${hightouch}.recommendation.variables.preheader"
      // Extract the most meaningful part
      if (varName && varName.includes(".")) {
        const parts = varName.split(".");
        // Use the last meaningful part (skip ${...} patterns)
        const meaningfulParts = parts.filter((p) => !p.includes("${"));
        varName =
          meaningfulParts[meaningfulParts.length - 1] || parts[0] || varName;
      }

      return `[${varName}]`;
    }

    return "[variable]";
  });

  // Handle if/else blocks - preserve else content
  result = result.replace(
    /\{%\s*if[^%]*%\}(.*?)\{%\s*else\s*%\}(.*?)\{%\s*endif\s*%\}/gs,
    "$2",
  );
  result = result.replace(
    /\{%\s*unless[^%]*%\}(.*?)\{%\s*else\s*%\}(.*?)\{%\s*endunless\s*%\}/gs,
    "$2",
  );

  // Remove if blocks without else
  result = result.replace(/\{%\s*if[^%]*%\}(.*?)\{%\s*endif\s*%\}/gs, "");
  result = result.replace(
    /\{%\s*unless[^%]*%\}(.*?)\{%\s*endunless\s*%\}/gs,
    "$2",
  );

  // Remove remaining liquid tags
  result = result.replace(/\{%[^%]*%\}/g, "");

  return result;
}

/**
 * Render liquid template for email preview
 *
 * @param html - The HTML template with liquid syntax
 * @param platform - The liquid platform variant: "braze", "iterable", or undefined for standard
 *
 * Renders with an empty data object, which allows liquid's built-in features to work:
 * - Default values: {{ variable | default: "Default" }} will show "Default"
 * - Conditionals: {% if variable %}...{% else %}...{% endif %} will use the else branch
 * - Filters and logic will execute normally
 *
 * For platform-specific templates (Braze/Iterable) that use incompatible syntax,
 * falls back to stripping liquid syntax to show static content.
 */
export async function renderLiquidEmailTemplate(
  html: string,
  platform?: "braze" | "iterable" | null,
): Promise<string> {
  if (!containsLiquidSyntax(html)) {
    return html;
  }

  // For Braze and Iterable, these platforms use liquid variants with platform-specific
  // syntax that liquidjs can't parse. Strip liquid syntax for preview instead.
  if (platform === "braze" || platform === "iterable") {
    return stripLiquidForPreview(html);
  }

  // Try standard liquid rendering for other cases
  try {
    const engine = new Liquid({
      strictFilters: false, // Allow undefined variables
      strictVariables: false, // Don't throw on undefined variables
      lenientIf: true, // Treat undefined as falsy in conditionals
    });

    // Render with empty data object - liquid will use defaults and else branches
    return await engine.parseAndRender(html, {});
  } catch {
    // If standard rendering fails, fall back to stripping syntax
    return stripLiquidForPreview(html);
  }
}
